import { EventPackage, SectionSeat } from "../../../models/AssignedSeating";
import {
    EventSeatmap,
    EventSeatmapPriceGroup,
    SeatAlignment,
    SeatmapSection,
} from "../../../models/Event";
import { calculateAngle } from "../../../utils/helpers";
import { PriceGroup } from "../SectionSeatComponent";

/**
 * Pure geometry extraction for the seatmap renderer. Produces a flat list of
 * per-seat draw records grouped by "frame" (one frame per SVG transform group:
 * a SECTION / TABLE / RECTANGLE_TABLE, or the svg root for top-level MANUAL
 * seats). The math mirrors the legacy per-alignment render functions exactly —
 * including their quirks (unfiltered vs isActive-filtered seat lists, idx vs
 * array-index positioning) — so the SVG and canvas paths stay in lockstep.
 */

export type SeatGeom = {
    seat: SectionSeat;
    /** Frame-local coordinates (same space the legacy SVG seat nodes used). */
    x: number;
    y: number;
    r: number;
    /** Seat counter-rotation; only meaningful for the SVG path (icons stay upright). */
    rotation: number;
    priceGroup: PriceGroup;
    message?: string;
    requireEntireSection: boolean;
    /**
     * Whether the popover's soldOut flag is derived from the seat's status
     * (grid/manual seats) or left undefined (table seats — legacy omits it).
     */
    deriveSoldOut: boolean;
    /** The section whose markup emitted this seat (ROW / MANUAL / table). */
    sourceSectionId: number;
};

export type SeatFrameKind = "root" | "section" | "table" | "rectTable";

export type SeatFrame = {
    /** Matches the data-seat-frame attribute of the probe rect in the SVG. */
    frameId: string;
    kind: SeatFrameKind;
    /** True when the frame's seats are only rendered while zoomed in (showSeats). */
    requiresShowSeats: boolean;
    /** Section owning the transform group; null for the svg root frame. */
    sectionId: number | null;
    seats: SeatGeom[];
    /** Bounding box of the seats (x±r / y±r) in frame-local coordinates. */
    localBounds: {
        minX: number;
        minY: number;
        maxX: number;
        maxY: number;
    } | null;
};

export function frameIdForSection(kind: SeatFrameKind, sectionId: number) {
    switch (kind) {
        case "section":
            return `seat-frame-sec-${sectionId}`;
        case "table":
            return `seat-frame-table-${sectionId}`;
        case "rectTable":
            return `seat-frame-rect-table-${sectionId}`;
        default:
            return "seat-frame-root";
    }
}

export const ROOT_FRAME_ID = "seat-frame-root";

type PriceGroupResolver = (seatId: number) => PriceGroup;

function makePriceGroupResolver(
    layout: EventSeatmap,
    eventPackage?: EventPackage,
): PriceGroupResolver {
    if (eventPackage?.priceGroups) {
        // Precompute GA seat-id sets so the per-seat filter is O(#priceGroups)
        // instead of O(#priceGroups × #seatIds).
        const gaSeatSets = new Map<number, Set<number>>();
        eventPackage.priceGroups.forEach((pg, i) => {
            if (pg.isGeneralAdmission) {
                gaSeatSets.set(i, new Set(pg.seatIds));
            }
        });
        return (seatId: number) => ({
            type: "package",
            priceGroups: eventPackage.priceGroups.filter(
                (pg, i) =>
                    !pg.isGeneralAdmission || gaSeatSets.get(i)!.has(seatId),
            ),
        });
    }

    // First matching price group wins, matching layout.priceGroups.find().
    const seatPriceGroups = new Map<number, EventSeatmapPriceGroup>();
    layout.priceGroups.forEach((pg) => {
        pg.seats.forEach((seatId) => {
            if (!seatPriceGroups.has(seatId)) {
                seatPriceGroups.set(seatId, pg);
            }
        });
    });
    return (seatId: number) => ({
        type: "seat",
        priceGroup: seatPriceGroups.get(seatId),
    });
}

function pushSeat(frame: SeatFrame, geom: SeatGeom) {
    frame.seats.push(geom);
    const minX = geom.x - geom.r;
    const maxX = geom.x + geom.r;
    const minY = geom.y - geom.r;
    const maxY = geom.y + geom.r;
    if (!frame.localBounds) {
        frame.localBounds = { minX, minY, maxX, maxY };
    } else {
        frame.localBounds.minX = Math.min(frame.localBounds.minX, minX);
        frame.localBounds.maxX = Math.max(frame.localBounds.maxX, maxX);
        frame.localBounds.minY = Math.min(frame.localBounds.minY, minY);
        frame.localBounds.maxY = Math.max(frame.localBounds.maxY, maxY);
    }
}

const byIdx = (a: SectionSeat, b: SectionSeat) => a.idx - b.idx;

export function buildSeatFrames(
    layout: EventSeatmap,
    eventPackage?: EventPackage,
): SeatFrame[] {
    const resolvePriceGroup = makePriceGroupResolver(layout, eventPackage);
    const frames: SeatFrame[] = [];
    const rootFrame: SeatFrame = {
        frameId: ROOT_FRAME_ID,
        kind: "root",
        requiresShowSeats: false,
        sectionId: null,
        seats: [],
        localBounds: null,
    };
    frames.push(rootFrame);

    function rowSeatGeom(
        s: SectionSeat,
        sec: SeatmapSection,
        parentSec: SeatmapSection,
        seatX: number,
        seatY: number,
    ): SeatGeom {
        return {
            seat: s,
            x: seatX,
            y: seatY,
            r: parentSec.seatRadius,
            rotation: parentSec.rotation,
            priceGroup: resolvePriceGroup(s.id),
            message: sec.message,
            requireEntireSection: false,
            deriveSoldOut: true,
            sourceSectionId: sec.id,
        };
    }

    function emitDefaultSeats(
        frame: SeatFrame,
        sec: SeatmapSection,
        parentSec: SeatmapSection,
    ) {
        const filteredSeats = sec.seats;
        [...filteredSeats].sort(byIdx).forEach((s) => {
            const numRows = parentSec.sections.length;
            const cols = filteredSeats.length;
            const properIndex = s.idx;
            const seatX =
                (cols / 2 - cols + properIndex + 0.5) * parentSec.seatSpacing;
            const seatY =
                (numRows / 2 - numRows + sec.idx + 0.5) * parentSec.rowSpacing;
            pushSeat(frame, rowSeatGeom(s, sec, parentSec, seatX, seatY));
        });
    }

    function emitCenteredSeats(
        frame: SeatFrame,
        sec: SeatmapSection,
        parentSec: SeatmapSection,
        rowsSorted: SeatmapSection[],
    ) {
        const numRows = parentSec.sections.length;
        const rowSpacing = parentSec.rowSpacing;

        const filteredSeats = sec.seats.filter((s) => s.isActive).sort(byIdx);

        const firstRow = rowsSorted[0];
        const firstRowSeats = [...firstRow.seats].sort(byIdx);
        const firstSeat = firstRowSeats[0];
        const lastSeat = firstRowSeats[firstRow.seats.length - 1];
        const P0 = {
            x:
                (sec.seats.length / 2 -
                    sec.seats.length +
                    firstSeat.idx +
                    0.5) *
                parentSec.seatSpacing,
            y: (numRows / 2 - numRows + firstSeat.idx + 0.5) * rowSpacing,
        };
        const P2 = {
            x:
                (sec.seats.length / 2 - sec.seats.length + lastSeat.idx + 0.5) *
                parentSec.seatSpacing,
            y: (numRows / 2 - numRows + firstSeat.idx + 0.5) * rowSpacing,
        };
        const P1 = {
            x: (P0.x + P2.x) / 2,
            y: parentSec.seatCurve,
        };
        const radius = Math.sqrt((P0.x - P1.x) ** 2 + (P0.y - P1.y) ** 2);
        const startAngle = calculateAngle(P1, P0);
        const endAngle = calculateAngle(P1, P2);

        const angleIncrement =
            (endAngle - startAngle) / (filteredSeats.length - 1);

        filteredSeats.forEach((s, sIdx) => {
            const angle = startAngle + angleIncrement * sIdx;
            const seatX = P1.x + radius * Math.cos(angle);
            const seatY =
                P1.y + radius * Math.sin(angle) + rowSpacing * sec.idx;
            pushSeat(frame, rowSeatGeom(s, sec, parentSec, seatX, seatY));
        });
    }

    function emitCenterConeSeats(
        frame: SeatFrame,
        sec: SeatmapSection,
        parentSec: SeatmapSection,
        overrideSeatSpacing: number,
    ) {
        const filteredSeats = sec.seats.filter((s) => s.isActive);
        [...filteredSeats].sort(byIdx).forEach((s, sIdx) => {
            const numRows = parentSec.sections.length;
            const usualNumCols = sec.seats.length;
            const numCols = filteredSeats.length;
            const curve = parentSec.seatCurve;

            let properIndex = sIdx;
            const cols = usualNumCols;
            if (usualNumCols !== numCols) {
                const diff = usualNumCols - numCols;
                properIndex = diff / 2 + sIdx;
            }

            const seatX =
                (cols / 2 - cols + properIndex + 0.5) * overrideSeatSpacing;
            let seatY =
                (numRows / 2 - numRows + sec.idx + 0.5) * parentSec.rowSpacing;

            const middleIndex = (cols - 1) / 2;
            const normalizedDistanceFromMiddle =
                (properIndex - middleIndex) / middleIndex;
            seatY -= curve * (1 - Math.pow(normalizedDistanceFromMiddle, 2));

            pushSeat(frame, rowSeatGeom(s, sec, parentSec, seatX, seatY));
        });
    }

    function emitLeftSeats(
        frame: SeatFrame,
        sec: SeatmapSection,
        parentSec: SeatmapSection,
    ) {
        const filteredSeats = sec.seats.filter((s) => s.isActive);
        [...filteredSeats].sort(byIdx).forEach((s, sIdx) => {
            const numRows = parentSec.sections.length;
            const cols = sec.seats.length;
            const properIndex = sIdx;
            const seatX =
                (cols / 2 - cols + properIndex + 0.5) * parentSec.seatSpacing;
            const seatY =
                (numRows / 2 - numRows + sec.idx + 0.5) * parentSec.rowSpacing;
            pushSeat(frame, rowSeatGeom(s, sec, parentSec, seatX, seatY));
        });
    }

    function emitRightSeats(
        frame: SeatFrame,
        sec: SeatmapSection,
        parentSec: SeatmapSection,
    ) {
        const filteredSeats = sec.seats.filter((s) => s.isActive);
        [...filteredSeats].sort(byIdx).forEach((s, sIdx) => {
            const numRows = parentSec.sections.length;
            const usualNumCols = sec.seats.length;
            const curve = parentSec.seatCurve;

            const properIndex = usualNumCols - sIdx - 1;
            const cols = usualNumCols;

            const seatX =
                (cols / 2 - cols + properIndex + 0.5) * parentSec.seatSpacing;
            let seatY =
                (numRows / 2 - numRows + sec.idx + 0.5) * parentSec.rowSpacing;

            const middleIndex = (cols - 1) / 2;
            const normalizedDistanceFromMiddle =
                (properIndex - middleIndex) / middleIndex;
            seatY -= curve * (1 - Math.pow(normalizedDistanceFromMiddle, 2));

            pushSeat(frame, rowSeatGeom(s, sec, parentSec, seatX, seatY));
        });
    }

    function emitSkewVerticalSeats(
        frame: SeatFrame,
        sec: SeatmapSection,
        parentSec: SeatmapSection,
    ) {
        const filteredSeats = sec.seats.filter((s) => s.isActive);
        [...filteredSeats].sort(byIdx).forEach((s) => {
            const numRows = parentSec.sections.length;
            const cols = sec.seats.length;
            const curve = parentSec.seatCurve;
            const skew = parentSec.skew;

            const properIndex = s.idx;

            const seatX =
                (cols / 2 - cols + properIndex + 0.5) * parentSec.seatSpacing;
            let seatY =
                (numRows / 2 - numRows + sec.idx + 0.5) * parentSec.rowSpacing;

            seatY -= skew * properIndex;

            const middleIndex = (cols - 1) / 2;
            const normalizedDistanceFromMiddle =
                (properIndex - middleIndex) / middleIndex;
            seatY -= curve * (1 - Math.pow(normalizedDistanceFromMiddle, 2));

            pushSeat(frame, rowSeatGeom(s, sec, parentSec, seatX, seatY));
        });
    }

    function emitSkewHorizontalSeats(
        frame: SeatFrame,
        sec: SeatmapSection,
        parentSec: SeatmapSection,
        rowsSorted: SeatmapSection[],
    ) {
        const filteredSeats = sec.seats.filter((s) => s.isActive);
        [...filteredSeats].sort(byIdx).forEach((s) => {
            const numRows = parentSec.sections.length;
            const cols = sec.seats.length;
            const curve = parentSec.seatCurve;

            const properIndex = s.idx;

            let seatX =
                (cols / 2 - cols + properIndex + 0.5) * parentSec.seatSpacing;
            seatX -=
                parentSec.skew * rowsSorted.findIndex((it) => it.id === sec.id);

            let seatY =
                (numRows / 2 - numRows + sec.idx + 0.5) * parentSec.rowSpacing;

            const middleIndex = (cols - 1) / 2;
            const normalizedDistanceFromMiddle =
                (properIndex - middleIndex) / middleIndex;
            seatY -= curve * (1 - Math.pow(normalizedDistanceFromMiddle, 2));

            pushSeat(frame, rowSeatGeom(s, sec, parentSec, seatX, seatY));
        });
    }

    function emitRow(
        frame: SeatFrame,
        sec: SeatmapSection,
        parentSec: SeatmapSection,
        rowsSorted: SeatmapSection[],
    ) {
        switch (parentSec.seatAlignment) {
            case SeatAlignment.DEFAULT:
                emitDefaultSeats(frame, sec, parentSec);
                break;
            case SeatAlignment.CENTER:
                emitCenteredSeats(frame, sec, parentSec, rowsSorted);
                break;
            case SeatAlignment.CENTER_CONE: {
                const firstRowWidth =
                    rowsSorted[0].seats.filter((it) => it.isActive).length *
                    parentSec.seatSpacing;
                const lastRowWidth =
                    rowsSorted[rowsSorted.length - 1].seats.filter(
                        (it) => it.isActive,
                    ).length * parentSec.seatSpacing;
                const diff = firstRowWidth - lastRowWidth;
                const idx = rowsSorted.findIndex((it) => it.id === sec.id);
                const interpolatedWidth =
                    firstRowWidth - (diff / (rowsSorted.length - 1)) * idx;
                const activeSeats = sec.seats.filter(
                    (it) => it.isActive,
                ).length;
                const seatSpacing = interpolatedWidth / activeSeats;
                emitCenterConeSeats(frame, sec, parentSec, seatSpacing);
                break;
            }
            case SeatAlignment.LEFT:
                emitLeftSeats(frame, sec, parentSec);
                break;
            case SeatAlignment.RIGHT:
                emitRightSeats(frame, sec, parentSec);
                break;
            case SeatAlignment.SKEW_VERTICAL:
                emitSkewVerticalSeats(frame, sec, parentSec);
                break;
            case SeatAlignment.SKEW_HORIZONTAL:
                emitSkewHorizontalSeats(frame, sec, parentSec, rowsSorted);
                break;
        }
    }

    function emitTable(sec: SeatmapSection, insideSection: boolean) {
        const frame: SeatFrame = {
            frameId: frameIdForSection("table", sec.id),
            kind: "table",
            requiresShowSeats: insideSection,
            sectionId: sec.id,
            seats: [],
            localBounds: null,
        };
        frames.push(frame);
        [...sec.seats].sort(byIdx).forEach((s, sIdx) => {
            const angle = (360 / sec.seats.length) * sIdx - 90;
            const x =
                Math.cos((angle * Math.PI) / 180) *
                (sec.rowSpacing + sec.seatSpacing);
            const y =
                Math.sin((angle * Math.PI) / 180) *
                (sec.rowSpacing + sec.seatSpacing);
            pushSeat(frame, {
                seat: s,
                x,
                y,
                r: sec.seatRadius,
                rotation: sec.rotation,
                priceGroup: resolvePriceGroup(s.id),
                message: sec.message,
                requireEntireSection: sec.requireEntireSection,
                deriveSoldOut: false,
                sourceSectionId: sec.id,
            });
        });
    }

    function emitRectangleTable(sec: SeatmapSection, insideSection: boolean) {
        const frame: SeatFrame = {
            frameId: frameIdForSection("rectTable", sec.id),
            kind: "rectTable",
            requiresShowSeats: insideSection,
            sectionId: sec.id,
            seats: [],
            localBounds: null,
        };
        frames.push(frame);

        const seatsPerSide = sec.config?.seatsPerSide ?? 1;
        const numSeats = sec.seats.length;
        const seatsPerEnd = Math.max(numSeats - seatsPerSide * 2, 0) / 2;
        const seats = [...sec.seats].sort(byIdx);

        const push = (s: SectionSeat, x: number, y: number) =>
            pushSeat(frame, {
                seat: s,
                x,
                y,
                r: sec.seatRadius,
                rotation: sec.rotation,
                priceGroup: resolvePriceGroup(s.id),
                message: sec.message,
                requireEntireSection: sec.requireEntireSection,
                deriveSoldOut: false,
                sourceSectionId: sec.id,
            });

        seats.slice(0, seatsPerSide).forEach((s, sIdx) => {
            const spaceBetweenSeats = sec.width / (seatsPerSide + 1);
            push(s, spaceBetweenSeats * (sIdx + 1), 0 - sec.seatSpacing);
        });
        seats
            .slice(seatsPerSide, seatsPerSide + seatsPerEnd)
            .forEach((s, sIdx) => {
                const spaceBetweenSeats = sec.height / (seatsPerEnd + 1);
                push(
                    s,
                    sec.width + sec.seatSpacing,
                    spaceBetweenSeats * (sIdx + 1),
                );
            });
        seats
            .slice(
                seatsPerSide + seatsPerEnd,
                seatsPerSide + seatsPerEnd + seatsPerSide,
            )
            .forEach((s, sIdx) => {
                const spaceBetweenSeats = sec.width / (seatsPerSide + 1);
                push(
                    s,
                    sec.width - spaceBetweenSeats * (sIdx + 1),
                    sec.height + sec.seatSpacing,
                );
            });
        seats
            .slice(
                seatsPerSide * 2 + seatsPerEnd,
                seatsPerSide * 2 + seatsPerEnd * 2,
            )
            .forEach((s, sIdx) => {
                const spaceBetweenSeats = sec.height / (seatsPerEnd + 1);
                push(
                    s,
                    0 - sec.seatSpacing,
                    sec.height - spaceBetweenSeats * (sIdx + 1),
                );
            });
    }

    function visit(
        sec: SeatmapSection,
        parentSec: SeatmapSection | null,
        currentFrame: SeatFrame,
        rowsSorted: SeatmapSection[] | null,
        insideSection: boolean,
    ) {
        if (sec.type === "MANUAL") {
            sec.sections.forEach((s) =>
                visit(s, sec, currentFrame, null, insideSection),
            );
            sec.seats.forEach((s) => {
                pushSeat(currentFrame, {
                    seat: s,
                    x: s.seatX ?? 0,
                    y: s.seatY ?? 0,
                    r: sec.seatRadius,
                    rotation: sec.rotation,
                    priceGroup: resolvePriceGroup(s.id),
                    message: sec.message,
                    requireEntireSection: false,
                    deriveSoldOut: true,
                    sourceSectionId: sec.id,
                });
            });
        } else if (parentSec && parentSec.type === "SECTION") {
            if (sec.type === "ROW") {
                emitRow(currentFrame, sec, parentSec, rowsSorted!);
            }
        } else if (sec.type === "SECTION") {
            const frame: SeatFrame = {
                frameId: frameIdForSection("section", sec.id),
                kind: "section",
                requiresShowSeats: true,
                sectionId: sec.id,
                seats: [],
                localBounds: null,
            };
            frames.push(frame);
            const rows = [...sec.sections].sort((a, b) => a.idx - b.idx);
            rows.forEach((s) => visit(s, sec, frame, rows, true));
        } else if (sec.type === "TABLE") {
            emitTable(sec, insideSection);
        } else if (sec.type === "RECTANGLE_TABLE") {
            emitRectangleTable(sec, insideSection);
        }
        // GENERAL sections stay in the SVG layer and carry no individual seats.
    }

    layout.seatmapTemplate.sections
        .filter((it) => it.type !== "GENERAL")
        .forEach((it) => visit(it, null, rootFrame, null, false));

    return frames;
}
